import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, NavLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import styles from "./ActivateAccount.module.css";
import { Typography, CircularProgress, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // 'processing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      setStatus("error");
      setErrorMessage("Invalid activation link. Missing token or email.");
      return;
    }

    const activate = async () => {
      try {
        const response = await axios.post("/api/account/activate-account", { token, email });
        if (response.data.success) {
          setStatus("success");
          enqueueSnackbar("Account activated! You can now sign in.", {
            variant: "success",
            autoHideDuration: 6000,
          });
          setTimeout(() => navigate("/login"), 3000);
        } else {
          throw new Error(response.data.error || "Activation failed");
        }
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.message ||
          "Account activation failed. Please try again or contact support.";
        setStatus("error");
        setErrorMessage(msg);
        enqueueSnackbar(msg, { variant: "error", autoHideDuration: 8000 });
      }
    };

    activate();
  }, [token, email]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={styles.accountNav}>
        <Typography className={styles.navText}>
          Sharing God's Love in Word and Deed
        </Typography>
      </div>
      <div className={styles.ActivateAccountMain}>
        <div id="app" className={styles.contentHalf}>
          <section className={styles.activationSection}>
            <img
              src="./Images/baptists-on-mission-logo.png"
              alt="Baptists on Mission Logo"
              className={styles.bomLogo}
            />

            {/* Processing */}
            {status === "processing" && (
              <div className={styles.loadingContainer}>
                <CircularProgress size={48} sx={{ color: "#003063" }} />
                <Typography variant="h6" className={styles.loadingText} sx={{ mt: 2 }}>
                  Activating your account…
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Please wait, this only takes a moment.
                </Typography>
              </div>
            )}

            {/* Success */}
            {status === "success" && (
              <div className={styles.successContainer}>
                <div className={styles.successMessage}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#2e7d32", mb: 1 }} />
                  <Typography variant="h4" className={styles.successTitle}>
                    Account Activated!
                  </Typography>
                  <Typography variant="body1" className={styles.successText} sx={{ mt: 1 }}>
                    Your account is ready. Welcome to Baptists on Mission!
                  </Typography>
                  <Typography variant="body2" className={styles.redirectText} sx={{ mt: 1 }}>
                    Redirecting you to sign in…
                  </Typography>
                  <Typography variant="caption" className={styles.manualRedirect} sx={{ mt: 1, display: "block" }}>
                    <NavLink to="/login">Click here to sign in now</NavLink>
                  </Typography>
                </div>
              </div>
            )}

            {/* Error */}
            {status === "error" && (
              <div className={styles.errorContainer}>
                <div className={styles.errorMessage}>
                  <ErrorOutlineIcon sx={{ fontSize: 48, color: "#c62828", mb: 1 }} />
                  <Typography variant="h4" className={styles.errorTitle}>
                    Activation Failed
                  </Typography>
                  <Typography variant="body1" className={styles.errorText} sx={{ mt: 1 }}>
                    {errorMessage}
                  </Typography>
                  <div className={styles.errorActions} style={{ marginTop: 16 }}>
                    <Typography variant="body2" className={styles.helpText}>
                      Need help? Contact us at (800) 395-5102 ext 5596
                    </Typography>
                    <Typography variant="caption" className={styles.loginLink} sx={{ display: "block", mt: 1 }}>
                      Already have an active account?{" "}
                      <NavLink to="/login">Sign In</NavLink>
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
        <img
          className={styles.activateAccountImg}
          src="./Images/create-account-page-image.jpg"
          alt="Baptists on Mission activate account"
        />
      </div>
    </>
  );
}
